import React from 'react';
import {InstructorDetails} from '@udemy-v2/browse-course';
import styles from './instructor-card.module.scss';
import classNames from 'classnames';

import {InstructorStats} from '../instructor-stats/instructor-stats.react-component';
import {InstructorDescription} from '../instructor-description/instructor-description.react-component';
import {generateInstructorId} from '../utils/instructor-utils';
import {useInstructorCommunityBannerInfo} from '@/lib/hooks/use-instructor-community-banner-info';
import {InstructorCommunityBanner} from '../../instructor-community/instructor-community-banner';
import {useUserHasOrganization} from '@/lib/hooks/use-user-has-organization';

export interface InstructorCardProps {
    instructor: InstructorDetails;
    index: number;
}

/**
 * Renders an instructor card displaying the instructor's image, name, and title
 *
 * @example
 * ```jsx
 * <InstructorCard instructor={instructor} index={0} />
 * ```
 */
export const InstructorCard: React.FC<InstructorCardProps> = ({instructor, index}) => {
    const {bannerInfo, hasCommunity} = useInstructorCommunityBannerInfo(instructor.id);
    const hasOrganization = useUserHasOrganization();

    return (
        <div className={classNames(styles['instructor-card'], 'ud-card')}>
            <div
                id={generateInstructorId(index)}
                className={styles['instructor-anchor-offset']}
                aria-hidden="true"
            />
            <div className={styles['instructor-info']}>
                <a
                    href={instructor.url}
                    className={classNames(
                        'ud-heading-lg ud-link-underline',
                        styles['instructor-name'],
                    )}
                >
                    {instructor.name}
                </a>
                {instructor.title && (
                    <p className={classNames('ud-text-md', styles['instructor-title'])}>
                        {instructor.title}
                    </p>
                )}
            </div>
            {!hasOrganization && (
                <InstructorStats instructor={instructor} showCommunityPoweredBadge={hasCommunity} />
            )}
            {!hasOrganization && hasCommunity && bannerInfo && (
                <InstructorCommunityBanner
                    instructorName={instructor.name}
                    instructorUrl={instructor.url}
                    bannerInfo={bannerInfo}
                />
            )}
            {instructor.description && (
                <InstructorDescription description={instructor.description} />
            )}
        </div>
    );
};
